import React from 'react';
import {AvForm, AvGroup} from 'availity-reactstrap-validation';
import DataTable from '../../shared/component/dataTable/DataTable';
import PurchaseRequisitionColumn from '../../shared/component/dataTable/columns/PurchaseRequisitionColumn.json';
import {Button} from 'reactstrap';
import GeneralUtils from '../../shared/utils/GeneralUtils';
import AvField from '../../shared/component/form/AvField';
import {PURCHASE_REQUISITION_APPROVAL_FILTER, PURCHASE_REQUISITION_STATUS} from '../../shared/utils/Constants';
import {resolvePurchaseRequisitionStatusFromQuery} from '../dasboard/dashboardMenuUtils';
import {queryTokenForStatus, rememberListReturnHash} from './PurchaseRequisitionListNav';
import '../../shared/styles/SettingsPage.css';

const LIST_FILTER_STORAGE_KEY = 'purchaseRequisition.listFilter';

/** Merged PR + PO page only (Delivery Note / Return keep their own menus). */
const MERGED_PAGE_STATUSES = PURCHASE_REQUISITION_STATUS.filter(item =>
    item.value === 'OPEN' || item.value === 'PURCHASE_ORDER'
);

function readStatusQueryFromHash() {
    const hash = window.location.hash || '';
    const queryPart = hash.includes('?') ? hash.substring(hash.indexOf('?') + 1) : '';
    return resolvePurchaseRequisitionStatusFromQuery(queryPart);
}

function pageTitleForStatus(requisitionStatus) {
    if (!requisitionStatus || requisitionStatus === 'OPEN') {
        return 'Purchase Requisition';
    }
    const match = PURCHASE_REQUISITION_STATUS.find(item => item.value === requisitionStatus);
    return match ? match.name : 'Purchase Requisition';
}

function loadStoredListFilter(lockedStatus) {
    if (lockedStatus) {
        const needsApproval = lockedStatus === 'DELIVERY_NOTE' || lockedStatus === 'RETURN';
        return {
            requisitionStatus: lockedStatus,
            approvedFilter: needsApproval ? 'false' : 'false',
            statusLocked: true
        };
    }
    try {
        const raw = sessionStorage.getItem(LIST_FILTER_STORAGE_KEY);
        if (!raw) {
            return {requisitionStatus: 'OPEN', approvedFilter: 'false', statusLocked: false};
        }
        const parsed = JSON.parse(raw);
        const mergedValues = MERGED_PAGE_STATUSES.map(item => item.value);
        // Unlocked list is PR+PO only — ignore DN/Return leftover from session.
        const requisitionStatus = mergedValues.includes(parsed?.requisitionStatus)
            ? parsed.requisitionStatus
            : 'OPEN';
        return {requisitionStatus, approvedFilter: 'false', statusLocked: false};
    } catch (e) {
        return {requisitionStatus: 'OPEN', approvedFilter: 'false', statusLocked: false};
    }
}

function saveStoredListFilter(requisitionStatus, approvedFilter, statusLocked) {
    if (statusLocked) {
        return;
    }
    try {
        sessionStorage.setItem(LIST_FILTER_STORAGE_KEY, JSON.stringify({
            requisitionStatus,
            approvedFilter
        }));
    } catch (e) {
        // ignore storage errors
    }
}

export default class PurchaseRequisition extends React.Component {
    constructor(props) {
        super(props);
        const lockedStatus = readStatusQueryFromHash();
        const stored = loadStoredListFilter(lockedStatus);
        this.state = {
            requisitionStatus: stored.requisitionStatus,
            approvedFilter: stored.approvedFilter,
            statusLocked: stored.statusLocked
        };
        this.refDataTable = null;
    }

    componentDidMount() {
        if (GeneralUtils.isMobile()) {
            const lockedStatus = readStatusQueryFromHash();
            window.location.href = lockedStatus
                ? '#/purchaseRequisitionMobile?' + queryTokenForStatus(lockedStatus)
                : '#/purchaseRequisitionMobile';
            return;
        }
        if (this.state.statusLocked) {
            rememberListReturnHash(this.state.requisitionStatus, true);
        }
        window.addEventListener('hashchange', this.syncStatusFromHash);
    }

    componentWillUnmount() {
        window.removeEventListener('hashchange', this.syncStatusFromHash);
    }

    syncStatusFromHash = () => {
        const lockedStatus = readStatusQueryFromHash();
        const next = loadStoredListFilter(lockedStatus);
        if (next.statusLocked) {
            rememberListReturnHash(next.requisitionStatus, true);
        }
        this.setState({
            requisitionStatus: next.requisitionStatus,
            approvedFilter: next.approvedFilter,
            statusLocked: next.statusLocked
        }, () => this.refreshDataTable());
    };

    setDataTableRef = (elem) => {
        this.refDataTable = elem;
        if (elem) {
            this.refreshDataTable();
        }
    };

    getListFields = () => {
        const showApproval = this.state.requisitionStatus === 'DELIVERY_NOTE'
            || this.state.requisitionStatus === 'RETURN';
        // Status is already chosen via combo / locked page title — hide Status column.
        return PurchaseRequisitionColumn.fields.map(field => {
            if (field.field === 'requisitionStatus') {
                return {...field, visible: false};
            }
            if (field.field === 'approvalDisplay') {
                return {...field, visible: showApproval};
            }
            return field;
        });
    };

    render() {
        if (GeneralUtils.isMobile()) {
            return null;
        }

        const showApprovalFilter = this.state.requisitionStatus === 'DELIVERY_NOTE'
            || this.state.requisitionStatus === 'RETURN';
        const pageTitle = pageTitleForStatus(this.state.requisitionStatus);

        return (
            <div className="settings-page settings-page--full col-md-12">
                <AvForm>
                    <div className="settings-page__shell">
                        <div className="settings-page__header">
                            <h1 className="settings-page__title">{pageTitle}</h1>
                        </div>
                        <div className="settings-page__panel">
                            <div className="settings-page__invoice-toolbar">
                                <div className="settings-page__invoice-toolbar-status">
                                    {!this.state.statusLocked &&
                                        <AvGroup className="mb-0">
                                            <AvField
                                                type="select"
                                                className="form-control"
                                                name="requisitionStatus"
                                                value={this.state.requisitionStatus}
                                                onChange={this.__handleChange}
                                            >
                                                {MERGED_PAGE_STATUSES.map(element => (
                                                    <option value={element.value} key={element.value}>
                                                        {element.name}
                                                    </option>
                                                ))}
                                            </AvField>
                                        </AvGroup>}
                                    {showApprovalFilter &&
                                        <AvGroup className="mb-0">
                                            <AvField
                                                type="select"
                                                className="form-control"
                                                name="approvedFilter"
                                                value={this.state.approvedFilter}
                                                onChange={this.__handleApprovalFilterChange}
                                            >
                                                {PURCHASE_REQUISITION_APPROVAL_FILTER.map(element => (
                                                    <option value={element.value} key={element.name}>
                                                        {element.name}
                                                    </option>
                                                ))}
                                            </AvField>
                                        </AvGroup>
                                    }
                                </div>
                            </div>

                            <div className="settings-page__toolbar settings-page__toolbar--start settings-page__toolbar--equal-height">
                                {this.state.requisitionStatus === 'OPEN' &&
                                    <Button
                                        color="outline-primary"
                                        className="settings-page__btn--add"
                                        style={{minWidth: '16.5rem'}}
                                        onClick={this.addNew}
                                    >
                                        <i className="pi pi-plus" aria-hidden="true"/>
                                        Create Purchase Requisition
                                    </Button>
                                }
                                {this.state.requisitionStatus === 'DELIVERY_NOTE' &&
                                    <Button
                                        color="outline-primary"
                                        className="settings-page__btn--add"
                                        style={{minWidth: '14rem'}}
                                        onClick={this.addNewDeliveryNote}
                                    >
                                        <i className="pi pi-plus" aria-hidden="true"/>
                                        Create Delivery Note
                                    </Button>
                                }
                                {this.state.requisitionStatus === 'RETURN' &&
                                    <Button
                                        color="outline-primary"
                                        className="settings-page__btn--add"
                                        style={{minWidth: '12rem'}}
                                        onClick={this.addNewReturn}
                                    >
                                        <i className="pi pi-plus" aria-hidden="true"/>
                                        Create Return
                                    </Button>
                                }
                            </div>

                            <div className="settings-page__table-wrap">
                                <DataTable
                                    ref={this.setDataTableRef}
                                    fields={this.getListFields()}
                                    objectName={PurchaseRequisitionColumn.name}
                                    dataURL="inventory/PurchaseRequisition/all"
                                    rows={10}
                                    autoLoad={false}
                                    selectionMode="single"
                                    onDataSelect={this.onDataSelect}
                                />
                            </div>
                        </div>
                    </div>
                </AvForm>
            </div>
        );
    }

    refreshDataTable = () => {
        const table = this.refDataTable;
        if (!table || typeof table.findAll !== 'function') {
            return;
        }
        const {requisitionStatus, approvedFilter, statusLocked} = this.state;
        saveStoredListFilter(requisitionStatus, approvedFilter, statusLocked);
        const filter = [
            {
                name: 'requisitionStatus',
                value: requisitionStatus,
                type: 'string',
                filterMatchMode: 'equals'
            }
        ];
        const filterElement = {requisitionStatus};
        const useApprovalFilter = requisitionStatus === 'DELIVERY_NOTE'
            || requisitionStatus === 'RETURN';
        if (useApprovalFilter) {
            // softrame: boolean equals must use type=select + subType=boolean (avoids lower() on BOOL)
            filter.push({
                name: 'approved',
                value: approvedFilter === 'true' ? 'true' : 'false',
                type: 'select',
                subType: 'boolean',
                filterMatchMode: 'equals'
            });
            filterElement.approved = approvedFilter === 'true' ? 'true' : 'false';
        }

        table.setState({filter, filterElement, first: 0, loading: true});
        const gridModel = {
            filter,
            sort: table.state.sort || [],
            pageNumber: 0,
            limit: table.state.rows
        };
        table.findAll(gridModel).then(response => {
            if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                table.setState({
                    data: response.data,
                    totalRecords: response.totalCount != null ? response.totalCount : 0,
                    loading: false
                });
            } else {
                table.setState({loading: false});
            }
        }).catch(() => table.setState({loading: false}));
    };

    onDataSelect = (data) => {
        if (data?.id) {
            saveStoredListFilter(this.state.requisitionStatus, this.state.approvedFilter, this.state.statusLocked);
            rememberListReturnHash(this.state.requisitionStatus, this.state.statusLocked);
            window.location.href = '#/purchaseRequisitionSave?' + data.id;
        }
    };

    addNew = () => {
        saveStoredListFilter(this.state.requisitionStatus, this.state.approvedFilter, this.state.statusLocked);
        rememberListReturnHash(this.state.requisitionStatus, this.state.statusLocked);
        window.location.href = '#/purchaseRequisitionSave?-1';
    };

    addNewDeliveryNote = () => {
        saveStoredListFilter(this.state.requisitionStatus, this.state.approvedFilter, this.state.statusLocked);
        rememberListReturnHash(this.state.requisitionStatus, this.state.statusLocked);
        window.location.href = '#/purchaseRequisitionSave?-1&mode=delivery';
    };

    addNewReturn = () => {
        saveStoredListFilter(this.state.requisitionStatus, this.state.approvedFilter, this.state.statusLocked);
        rememberListReturnHash(this.state.requisitionStatus, this.state.statusLocked);
        window.location.href = '#/purchaseRequisitionSave?-1&mode=return';
    };

    __handleChange = (e) => {
        if (this.state.statusLocked) {
            return;
        }
        const value = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        const allowed = MERGED_PAGE_STATUSES.some(item => item.value === value);
        if (!allowed) {
            return;
        }
        this.setState({
            requisitionStatus: value,
            approvedFilter: 'false'
        }, () => this.refreshDataTable());
    };

    __handleApprovalFilterChange = (e) => {
        const value = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        this.setState({approvedFilter: value}, () => this.refreshDataTable());
    };
}
